import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { closeConfirmationDialogAction } from 'store/global/actions';

import { RetryActionPopupTestIds } from './RetryActionPopup.testIds';

const defaultObject = {};

const truncatedTooltipSx = {
  fontSize: '12px',
  fontWeight: 400,
  maxHeight: '50vh',
  overflow: 'auto',
  wordBreak: 'break-all',
};

export const messageStyles = {
  whiteSpace: 'normal',
  display: '-webkit-box',
  textAlign: 'center',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 10,
  overflow: 'hidden',
  overflowWrap: 'break-word',
  maxWidth: '100%',
};

const RetryActionPopup = ({
  isLoading,
  setIsLoading,
  catchHandlerProps = defaultObject,
  onCatch = _.noop,
  onRetryAction,
  message,
}) => {
  const dispatch = useDispatch();

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral({
    tooltipSx: truncatedTooltipSx,
  });

  const onTryAgain = useCallback(() => {
    setIsLoading(true);

    return onRetryAction({})
      .then(() => dispatch(closeConfirmationDialogAction()))
      .catch(({ response }) => {
        dispatch(closeConfirmationDialogAction());

        return onCatch({ ...catchHandlerProps, response });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setIsLoading, onRetryAction, dispatch, onCatch, catchHandlerProps]);

  return (
    <Box
      className="flex flex-col items-center gap-24 w-full"
      onMouseLeave={onResetTooltipState}
      onMouseOver={onMouseOver}
    >
      <TruncatedProvider />
      <Typography data-truncated component="h5" sx={messageStyles} variant="bold16">
        {message}
      </Typography>
      <Button
        className="flex-1 self-centre"
        data-testid={RetryActionPopupTestIds.TRY_AGAIN_BUTTON}
        disabled={isLoading}
        size="large"
        onClick={onTryAgain}
      >
        Try again
      </Button>
    </Box>
  );
};

RetryActionPopup.propTypes = {
  ...WithLoadingPropTypes,
  message: PropTypes.string.isRequired,
  onRetryAction: PropTypes.func.isRequired,
  catchHandlerProps: PropTypes.object,
  onCatch: PropTypes.func,
};

export default compose(withLoading, memo)(RetryActionPopup);
